import React, { useId, useState } from 'react'

import { Cancel01Icon } from '@hugeicons/react'

import { AppIcon } from '@/shared/ui/icons'
import {
  Dialog,
  DialogContent,
  DialogTitle
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

// Larguras a partir de `sm` apenas: no mobile o `DialogContent` ocupa a tela
// inteira, e um `max-w-*` sem prefixo venceria essa regra na mesclagem do
// tailwind-merge.
const SIZE_MAP = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  fullscreen:
    'max-w-full w-full h-full rounded-none sm:max-w-full sm:h-full sm:rounded-none'
}

const ModalHeader = ({
  children,
  className = '',
  hideButtonClassName = '',
  hideButtonIconClassName = '',
  onHide,
  ...rest
}) => (
  // `shrink-0`: cabeçalho fixo só é fixo se não for espremido quando o
  // conteúdo excede a altura do diálogo.
  <div
    className={cn('flex shrink-0 flex-wrap text-center border-0', className)}
    {...rest}
  >
    {onHide && (
      <div className="flex w-full justify-end border-0 pb-0 relative">
        <button
          type="button"
          className={cn(
            'absolute top-0 right-0 flex items-center justify-center w-8 h-8 rounded-lg bg-transparent border-0 text-gray-700 hover:text-gray-800 cursor-pointer',
            hideButtonClassName
          )}
          onClick={onHide}
        >
          <Cancel01Icon
            variant="duotone"
            size={20}
            className={hideButtonIconClassName}
          />
        </button>
      </div>
    )}
    {/* `items-center`: sem isso, um filho de largura fixa (ex. `Modal.Icon`)
        cai pra esquerda — `text-center` centraliza o texto dentro do bloco,
        não o bloco dentro do container. */}
    <div className="flex flex-col items-center m-2 xl:m-4 w-full pt-3">
      {children}
    </div>
  </div>
)

const ModalIcon = ({ className = '', name, ...rest }) => (
  <AppIcon name={name} className={cn('text-3xl mb-4', className)} {...rest} />
)

/**
 * Título visível do diálogo — e o nome acessível dele.
 *
 * É o `DialogTitle` do Radix, não um `<span>`: assim o Radix conecta sozinho o
 * `aria-labelledby` do `DialogContent`, o elemento vira heading de verdade, e
 * quem já renderizava `Modal.Title` ganha nome acessível sem mudar nada.
 * Use `title` no `Modal` apenas quando não houver título visível.
 */
const ModalTitle = ({ className = '', children, ...rest }) => (
  <DialogTitle className={cn('text-2xl font-bold mb-2 block', className)} {...rest}>
    {children}
  </DialogTitle>
)

const ModalSubtitle = ({ children, ...rest }) => (
  <div className="text-muted-foreground font-semibold text-sm" {...rest}>
    {children}
  </div>
)

const ModalBody = ({
  className = '',
  noPaddings = undefined,
  customPaddings = undefined,
  children = undefined,
  ...rest
}) => (
  <div
    className={cn(
      customPaddings ? '' : noPaddings ? 'p-0' : 'px-10 lg:px-[3.75rem] pb-10',
      // `min-h-0` é o que permite o item de flex encolher abaixo do conteúdo e
      // de fato rolar — sem ele o `overflow-y-auto` não tem efeito e o
      // `max-h` do DialogContent volta a cortar. `flex-1` faz o corpo ser o
      // único a ceder, preservando cabeçalho e rodapé visíveis.
      'min-h-0 flex-1 overflow-y-auto',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const ModalFooter = ({ className = '', noPaddings = undefined, children = undefined, ...rest }) => (
  <div
    className={cn(
      // `shrink-0` é o que garante o rodapé fixo: como item de flex ele é
      // encolhível por padrão, então num diálogo mais alto que a viewport o
      // navegador espreme o rodapé em vez de ceder o corpo, e o CTA some.
      // Quem cede é o corpo, que é `flex-1 min-h-0` e tem para onde rolar.
      'flex shrink-0 border-0 justify-center pt-0',
      // `pb-6` no mobile: sem ele o rodapé fica colado na borda do diálogo.
      noPaddings ? 'p-0' : 'px-10 pb-6 lg:px-[3.75rem] lg:pb-10',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const Modal = ({
  children,
  className = '',
  show = false,
  onHide = undefined,
  size = undefined,
  centered = false,
  backdrop = true,
  keyboard = true,
  /** Nome acessível do diálogo. Ver nota sobre obrigatoriedade abaixo. */
  title = undefined,
  'aria-label': ariaLabel = undefined,
  ...rest
}) => {
  const sizeClass = SIZE_MAP[size] || 'sm:max-w-lg'
  const hasTitleProp = title != null && title !== ''

  // Sem `onHide`, não existe caminho de fechamento — nem ✕, nem Escape, nem
  // clique no backdrop. Um diálogo bloqueante é legítimo (o gate de auth é um),
  // mas expor controle inoperante não é.
  const canClose = typeof onHide === 'function'

  // Só quando não há título nenhum: o Radix aponta `aria-labelledby` para o id
  // do DialogTitle mesmo quando nenhum é renderizado, e referência pendurada
  // deixa o diálogo sem nome. Aqui a referência é anulada e o rótulo vem do
  // `aria-label`.
  const labelOnlyProps =
    !hasTitleProp && ariaLabel
      ? { 'aria-label': ariaLabel, 'aria-labelledby': undefined }
      : {}

  // Allowlist em vez de espalhar `rest`. Consumidores como `Modal.Root` e
  // `showModal` repassam props de domínio adiante; espalhá-las no DialogContent
  // as levaria ao DOM, e o React reclamaria de atributo desconhecido — foi o
  // que aconteceu com o `dialogClassName` herdado da era Bootstrap. Só passam
  // adiante os atributos que fazem sentido num nó de diálogo.
  const passThrough = Object.fromEntries(
    Object.entries(rest).filter(([k]) => /^(aria-|data-|role$|id$)/.test(k))
  )

  return (
    <Dialog open={show ?? false} onOpenChange={open => !open && onHide?.()}>
      <DialogContent
        showClose={canClose}
        onEscapeKeyDown={canClose ? undefined : e => e.preventDefault()}
        onPointerDownOutside={canClose ? undefined : e => e.preventDefault()}
        aria-describedby={undefined}
        className={cn(sizeClass, 'p-0 sm:rounded-[0.9rem]', className)}
        {...passThrough}
        {...labelOnlyProps}
      >
        {/* Título só-para-leitor quando o diálogo não exibe um visível. Quando
            exibe, o consumidor usa `Modal.Title`, que já é o DialogTitle. */}
        {hasTitleProp && <DialogTitle className="sr-only">{title}</DialogTitle>}
        {children}
      </DialogContent>
    </Dialog>
  )
}

const EasyModalTrigger = ({ children, className = '' }) => {
  const [show, setShow] = useState(false)
  return (
    <>
      <button onClick={() => setShow(true)}>{children}</button>
      <Dialog open={show} onOpenChange={open => !open && setShow(false)}>
        <DialogContent className={cn('rounded-[0.9rem]', className)}>
          <span>conteúdo</span>
        </DialogContent>
      </Dialog>
    </>
  )
}

Modal.Header = ModalHeader
Modal.Icon = ModalIcon
Modal.Title = ModalTitle
Modal.Subtitle = ModalSubtitle
Modal.SubTitle = ModalSubtitle
Modal.Body = ModalBody
Modal.Footer = ModalFooter

export { Modal, EasyModalTrigger }
